import React from 'react';
import type { SecretFlag } from '@volley/crucible-ipc-types/diagnostics-secret-scan';

/**
 * Read-only preview pane for a diagnostic bundle's rendered text,
 * with inline highlight bars marking secret-scan flag ranges.
 *
 * Cloud-VM supportability TDD §C-1 "Renderer UX". The pane renders
 * the historical paste-able shape (output of `renderBundleAsText`)
 * so support playbooks stay applicable. Lines without flags render
 * unchanged; flagged lines get a red gutter mark + a tooltip that
 * names the matched pattern.
 *
 * The pane is intentionally small (max-height with overflow) so the
 * Copy button + the SecretFlagBanner + the "I've reviewed" checkbox
 * stay in view without scrolling — a user who scrolls past the banner
 * to hit Copy is exactly the failure mode this gate exists to prevent.
 */

export interface BundlePreviewProps {
  text: string;
  flags: ReadonlyArray<SecretFlag>;
}

export function BundlePreview(props: BundlePreviewProps): React.JSX.Element {
  const lines = React.useMemo(() => props.text.split('\n'), [props.text]);
  const flagsByLine = React.useMemo(() => {
    const map = new Map<number, SecretFlag[]>();
    for (const flag of props.flags) {
      const existing = map.get(flag.lineIndex);
      if (existing) existing.push(flag);
      else map.set(flag.lineIndex, [flag]);
    }
    return map;
  }, [props.flags]);

  return (
    <pre
      data-testid="bundle-preview"
      className="max-h-72 overflow-auto rounded-md border border-white/10 bg-black/30 p-3 text-[11px] leading-relaxed text-white/70"
    >
      {lines.map((line, idx) => {
        const lineFlags = flagsByLine.get(idx);
        if (!lineFlags || lineFlags.length === 0) {
          return (
            <div key={idx} className="whitespace-pre-wrap break-all">
              {line || ' '}
            </div>
          );
        }
        return (
          <div
            key={idx}
            data-testid="bundle-preview-flagged-line"
            data-line-index={idx}
            title={lineFlags.map((f) => f.patternLabel).join(', ')}
            className="-mx-3 border-l-2 border-rose-400/70 bg-rose-500/10 px-3 whitespace-pre-wrap break-all text-rose-100"
          >
            {line || ' '}
          </div>
        );
      })}
    </pre>
  );
}
